import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ClickAwayListener, Grow, Popper } from '@material-ui/core'
import SelectLabel from '../../../DenaliAnalysis/DenaliScopePicker/SelectLabel'
import InlineSearchInput from '../../../DenaliAnalysis/DenaliScopePicker/InlineSearchInput'
import SydButton from '../../../../commonDesign/Button'
import Switch from '../../../../molecules/Switch'
import { KEYS } from '../../../../../constants'
import { householdClientIdFiltersEqual } from './queryBuilders'
import { useUserLoginActivityContext } from './UserLoginActivityProvider'

const EMPTY_SELECTED_HOUSEHOLD_CLIENT_IDS = []

const triggerStyle = {
  minWidth: '200px',
  padding: '6px 10px',
  fontSize: 'var(--font-size-sm)',
  border: 'var(--border-neutral-thin)',
  borderRadius: 'var(--border-radius-sm)'
}

const scrollListStyle = {
  maxHeight: '280px',
  overflowY: 'auto',
  border: '1px solid var(--color-neutral-200)',
  borderRadius: '6px',
  padding: '4px 0'
}

const rowStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '3px 10px',
  fontSize: 'var(--font-size-sm)'
}

const loadingStyle = {
  padding: '12px',
  fontSize: 'var(--font-size-sm)',
  color: 'var(--color-neutral-600)',
  textAlign: 'center'
}

const emptyStyle = {
  padding: '12px',
  fontSize: 'var(--font-size-sm)',
  color: 'var(--color-neutral-600)',
  textAlign: 'center'
}

const searchRowStyle = {
  padding: '0 0 4px'
}

const footerStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: '8px',
  padding: '8px',
  borderTop: '1px solid var(--color-neutral-200)',
  background: 'var(--color-neutral-100)'
}

function useFilteredOptions (searchText, options) {
  return useMemo(() => {
    const q = searchText.trim().toLowerCase()
    if (!q) return options
    return options.filter((o) => (o.label ?? '').toLowerCase().includes(q))
  }, [searchText, options])
}

function buildSummaryLabel (isLoading, selectedIds) {
  if (isLoading) return 'Loading households…'
  const count = selectedIds?.length ?? 0
  if (count === 0) return 'All households'
  return count === 1 ? '1 household' : `${count} households`
}

export default function HouseholdMultiSelect () {
  const {
    householdOptions,
    selectedHouseholdClientIds,
    householdsLoading,
    commitSelectedHouseholds
  } = useUserLoginActivityContext()

  const anchorRef = useRef(null)
  const [open, setOpen] = useState(false)
  const [searchText, setSearchText] = useState('')
  const [draftSelectedIds, setDraftSelectedIds] = useState([])

  const committedSelected = useMemo(
    () => selectedHouseholdClientIds || EMPTY_SELECTED_HOUSEHOLD_CLIENT_IDS,
    [selectedHouseholdClientIds]
  )

  const selectedSet = useMemo(() => new Set(draftSelectedIds), [draftSelectedIds])
  const filteredOptions = useFilteredOptions(searchText, householdOptions)

  const summaryLabel = useMemo(
    () => buildSummaryLabel(householdsLoading, committedSelected),
    [householdsLoading, committedSelected]
  )

  const isDirty = useMemo(() => {
    if (!open) {
      return false
    }
    return !householdClientIdFiltersEqual(draftSelectedIds, committedSelected)
  }, [open, draftSelectedIds, committedSelected])

  const closeWithoutCommit = useCallback(() => {
    setOpen(false)
    setSearchText('')
  }, [])

  const openPopover = useCallback(() => {
    setDraftSelectedIds([...committedSelected])
    setOpen(true)
    setSearchText('')
  }, [committedSelected])

  const handleToggleOpen = useCallback(() => {
    if (open) {
      closeWithoutCommit()
      return
    }
    openPopover()
  }, [open, closeWithoutCommit, openPopover])

  const handleApply = useCallback((e) => {
    e?.stopPropagation?.()
    if (!isDirty) {
      closeWithoutCommit()
      return
    }
    commitSelectedHouseholds(draftSelectedIds)
    closeWithoutCommit()
  }, [isDirty, draftSelectedIds, commitSelectedHouseholds, closeWithoutCommit])

  const handleSearchChange = useCallback((value) => {
    setSearchText(value)
  }, [])

  const handleHouseholdToggle = useCallback((clientId) => {
    setDraftSelectedIds((prev) => {
      const selected = new Set(prev)
      if (selected.has(clientId)) {
        selected.delete(clientId)
      } else {
        selected.add(clientId)
      }
      return Array.from(selected)
    })
  }, [])

  const handleResetDraft = useCallback((e) => {
    e?.stopPropagation?.()
    setDraftSelectedIds([])
  }, [])

  const isDraftDefault = draftSelectedIds.length === 0

  useEffect(() => {
    if (!open) return undefined
    const handler = (e) => {
      if (e.which === KEYS.ESC) {
        closeWithoutCommit()
        e.stopPropagation()
        e.preventDefault()
      }
    }
    window.addEventListener('keydown', handler, false)
    return () => window.removeEventListener('keydown', handler, false)
  }, [open, closeWithoutCommit])

  const renderMenu = useCallback(({ TransitionProps }) => (
    <Grow {...TransitionProps} style={{ transformOrigin: 'top' }}>
      <div className='opal-drop-menu below select-none' style={{ minWidth: '280px' }}>
        <ClickAwayListener onClickAway={closeWithoutCommit}>
          <div>
            <div style={searchRowStyle}>
              <InlineSearchInput onChange={handleSearchChange} />
            </div>
            {householdsLoading ? (
              <div style={{ ...scrollListStyle, ...loadingStyle }}>Loading households…</div>
            ) : (
              <div style={scrollListStyle}>
                {filteredOptions.length === 0 ? (
                  <div style={emptyStyle}>No results</div>
                ) : (
                  filteredOptions.map((opt) => (
                    <div key={opt.value} style={rowStyle}>
                      <span>{opt.label}</span>
                      <Switch
                        checked={selectedSet.has(opt.value)}
                        onChange={() => handleHouseholdToggle(opt.value)}
                      />
                    </div>
                  ))
                )}
              </div>
            )}
            <div style={footerStyle}>
              <SydButton
                variant='ghost'
                size='sm'
                onClick={handleResetDraft}
                disabled={isDraftDefault || householdsLoading}
              >
                Reset
              </SydButton>
              <SydButton
                variant='primary'
                size='sm'
                onClick={handleApply}
                disabled={!isDirty || householdsLoading}
              >
                Apply
              </SydButton>
            </div>
          </div>
        </ClickAwayListener>
      </div>
    </Grow>
  ), [
    closeWithoutCommit,
    handleSearchChange,
    handleResetDraft,
    isDraftDefault,
    isDirty,
    householdsLoading,
    filteredOptions,
    selectedSet,
    handleHouseholdToggle,
    handleApply
  ])

  return (
    <>
      <SelectLabel
        ref={anchorRef}
        open={open}
        onClick={handleToggleOpen}
        isLoading={householdsLoading}
        style={triggerStyle}
      >
        {summaryLabel}
      </SelectLabel>
      <Popper
        transition
        open={open}
        placement='bottom-start'
        className='z-index-dropdown'
        anchorEl={anchorRef.current}
      >
        {renderMenu}
      </Popper>
    </>
  )
}
